import React, { useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
  Divider,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Trans, useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import TermsModal from "./TermsModal";

const schema = yup
  .object({
    name: yup
      .string()
      .trim()
      .min(2, "login.nameTooShort")
      .max(50, "login.nameTooLong")
      .required("login.nameRequired"),
    email: yup
      .string()
      .email("login.emailInvalid")
      .required("login.emailRequired"),
    password: yup
      .string()
      .min(8, "login.passwordTooShort")
      .matches(/(?=.*[A-Z])(?=.*[!@#$%^&*])/, "login.passwordWeak")
      .required("login.passwordRequired"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], "login.passwordsMustMatch")
      .required("login.confirmPasswordRequired"),
    termsAccepted: yup.boolean().oneOf([true], "login.termsRequired"),
  })
  .required();

export type AccountCreationFormValues = yup.InferType<typeof schema>;

interface Props {
  onSubmit: SubmitHandler<AccountCreationFormValues>;
  loading: boolean;
  switchToLogin: () => void;
  googleAuthEnabled?: boolean;
}

const fieldAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

const AccountCreationForm: React.FC<Props> = ({
  onSubmit,
  loading,
  switchToLogin,
  googleAuthEnabled = false,
}) => {
  const { t } = useTranslation();
  const [openTerms, setOpenTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const cleanEmail = (value: string) => {
    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^\w@.-]/g, "");
  };

  const cleanPassword = (value: string) => {
    return value.trim().replace(/\s+/g, "");
  };

  const cleanName = (value: string) => {
    return value.trim().replace(/\s+/g, " ");
  };

  const cleanNameForTyping = (value: string) => {
    // Durante a digitação, permite espaços mas limita espaços excessivos
    return value.replace(/\s{4,}/g, "   "); // Limita no máximo 3 espaços consecutivos
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AccountCreationFormValues>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
  });

  const processedOnSubmit = (data: AccountCreationFormValues) => {
    const cleanedData = {
      name: cleanName(data.name),
      email: cleanEmail(data.email),
      password: cleanPassword(data.password),
      confirmPassword: cleanPassword(data.confirmPassword),
      termsAccepted: data.termsAccepted,
    };
    onSubmit(cleanedData);
  };

  const handleSwitchToLogin = () => {
    setIsTransitioning(true);
    reset();
    setTimeout(() => {
      switchToLogin();
    }, 0);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(processedOnSubmit)}
        sx={{ mt: 2, maxWidth: 500, mx: "auto" }}
      >
        <Controller
          name="name"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <motion.div {...fieldAnim} transition={{ delay: 0.1 }}>
              <TextField
                {...field}
                label={t("login.name")}
                variant="outlined"
                size="medium"
                margin="normal"
                fullWidth
                error={!isTransitioning && !!errors.name}
                helperText={
                  !isTransitioning && errors.name && t(errors.name.message!)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2f2361",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2f2361",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#718096",
                    "&.Mui-focused": {
                      color: "#2f2361",
                    },
                  },
                }}
                onChange={(e) => {
                  const cleaned = cleanNameForTyping(e.target.value);
                  field.onChange(cleaned);
                }}
                onBlur={(e) => {
                  const cleaned = cleanName(e.target.value);
                  field.onChange(cleaned);
                  field.onBlur();
                }}
              />
            </motion.div>
          )}
        />

        <Controller
          name="email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <motion.div {...fieldAnim} transition={{ delay: 0.2 }}>
              <TextField
                {...field}
                label={t("login.email")}
                variant="outlined"
                size="medium"
                margin="normal"
                fullWidth
                error={!isTransitioning && !!errors.email}
                helperText={
                  !isTransitioning && errors.email && t(errors.email.message!)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2f2361",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2f2361",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#718096",
                    "&.Mui-focused": {
                      color: "#2f2361",
                    },
                  },
                }}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/^\s+/, "");
                  e.target.value = cleanValue;
                  field.onChange(cleanValue);
                }}
                onBlur={(e) => {
                  const cleaned = cleanEmail(e.target.value);
                  field.onChange(cleaned);
                  field.onBlur();
                }}
              />
            </motion.div>
          )}
        />

        <Controller
          name="password"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <motion.div {...fieldAnim} transition={{ delay: 0.3 }}>
              <TextField
                {...field}
                type={showPassword ? "text" : "password"}
                label={t("login.password")}
                variant="outlined"
                size="medium"
                margin="normal"
                fullWidth
                error={!isTransitioning && !!errors.password}
                helperText={
                  !isTransitioning &&
                  errors.password &&
                  t(errors.password.message!)
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        size="small"
                        sx={{ color: "#718096" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2f2361",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2f2361",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#718096",
                    "&.Mui-focused": {
                      color: "#2f2361",
                    },
                  },
                }}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/^\s+/, "");
                  e.target.value = cleanValue;
                  field.onChange(cleanValue);
                }}
                onBlur={(e) => {
                  const cleaned = cleanPassword(e.target.value);
                  field.onChange(cleaned);
                  field.onBlur();
                }}
              />
            </motion.div>
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <motion.div {...fieldAnim} transition={{ delay: 0.4 }}>
              <TextField
                {...field}
                type={showConfirm ? "text" : "password"}
                label={t("login.confirmPassword")}
                variant="outlined"
                size="medium"
                margin="normal"
                fullWidth
                error={!isTransitioning && !!errors.confirmPassword}
                helperText={
                  !isTransitioning &&
                  errors.confirmPassword &&
                  t(errors.confirmPassword.message!)
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirm((v) => !v)}
                        size="small"
                        sx={{ color: "#718096" }}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2f2361",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2f2361",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#718096",
                    "&.Mui-focused": {
                      color: "#2f2361",
                    },
                  },
                }}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/^\s+/, "");
                  e.target.value = cleanValue;
                  field.onChange(cleanValue);
                }}
                onBlur={(e) => {
                  const cleaned = cleanPassword(e.target.value);
                  field.onChange(cleaned);
                  field.onBlur();
                }}
              />
            </motion.div>
          )}
        />

        <Controller
          name="termsAccepted"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControl
              error={!isTransitioning && !!errors.termsAccepted}
              component="fieldset"
              sx={{ mt: 2 }}
            >
              <FormControlLabel
                control={
                  <Checkbox {...field} checked={field.value} size="small" />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                    <Trans
                      i18nKey="login.acceptTerms"
                      components={[
                        <Link
                          key="0"
                          component="button"
                          onClick={() => setOpenTerms(true)}
                          sx={{
                            p: 0,
                            textDecoration: "underline",
                            fontSize: "0.75rem",
                          }}
                        />,
                      ]}
                    />
                  </Typography>
                }
              />
              {!isTransitioning && errors.termsAccepted && (
                <FormHelperText sx={{ fontSize: "0.75rem" }}>
                  {t(errors.termsAccepted.message!)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <motion.div {...fieldAnim} transition={{ delay: 0.5 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting || loading}
            sx={{
              background: "linear-gradient(135deg, #2f2361 0%, #1e1640 100%)",
              color: "white",
              fontWeight: "700",
              fontSize: "1.1rem",
              py: 2.5,
              px: 8,
              borderRadius: "20px",
              textTransform: "none",
              boxShadow: "0 12px 40px rgba(47, 35, 97, 0.4)",
              mb: 2,
              mt: 3,
              position: "relative",
              overflow: "hidden",
              border: "none",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                opacity: 0,
                transition: "opacity 0.3s ease",
              },
              "&:hover": {
                background: "linear-gradient(135deg, #1e1640 0%, #2f2361 100%)",
                boxShadow: "0 16px 50px rgba(47, 35, 97, 0.5)",
                transform: "translateY(-3px)",
                "&::before": {
                  opacity: 1,
                },
              },
              "&:active": {
                transform: "translateY(-1px)",
                boxShadow: "0 8px 30px rgba(47, 35, 97, 0.4)",
              },
              "&:disabled": {
                background: "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                boxShadow: "0 4px 15px rgba(160, 174, 192, 0.3)",
                transform: "none",
                "&::before": {
                  opacity: 0,
                },
              },
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            }}
          >
            {isSubmitting || loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              t("login.createAccount")
            )}
          </Button>
        </motion.div>

        <motion.div {...fieldAnim} transition={{ delay: 0.6 }}>
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: googleAuthEnabled ? 2 : 4 }}
          >
            {t("login.alreadyHaveAccount")}{" "}
            <Link
              component="button"
              onClick={handleSwitchToLogin}
              sx={{ fontWeight: "bold" }}
            >
              {t("login.signIn")}
            </Link>
          </Typography>
        </motion.div>

        {/* Divider OU - apenas se Google estiver habilitado */}
        {googleAuthEnabled && (
          <motion.div {...fieldAnim} transition={{ delay: 0.6 }}>
            <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
              <Divider sx={{ flexGrow: 1, borderColor: "#e2e8f0" }} />
              <Typography
                sx={{
                  mx: 2,
                  color: "#718096",
                  fontSize: "0.8rem",
                  fontWeight: "500",
                }}
              >
                {t("login.or")}
              </Typography>
              <Divider sx={{ flexGrow: 1, borderColor: "#e2e8f0" }} />
            </Box>
          </motion.div>
        )}

        {/* Login com Google - apenas se estiver habilitado */}
        {googleAuthEnabled && (
          <motion.div {...fieldAnim} transition={{ delay: 0.65 }}>
            <Button
              fullWidth
              variant="outlined"
              size="medium"
              onClick={() => {
                window.location.href = "/api/auth/google/login";
              }}
              startIcon={
                <Box
                  component="img"
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  sx={{ width: 18, height: 18 }}
                />
              }
              sx={{
                py: 1.25,
                px: 2,
                borderRadius: "8px",
                borderColor: "#e2e8f0",
                backgroundColor: "#ffffff",
                color: "#4a5568",
                fontWeight: "600",
                fontSize: "0.9rem",
                textTransform: "none",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#cbd5e0",
                  backgroundColor: "#f7fafc",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                },
              }}
            >
              {t("login.signInWithGoogle")}
            </Button>
          </motion.div>
        )}
      </Box>

      <TermsModal open={openTerms} onClose={() => setOpenTerms(false)} />
    </>
  );
};

export default AccountCreationForm;
